import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MapPin, ShieldCheck, Clock, XCircle, CheckCircle2, Loader2 } from "lucide-react";

const GST_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export default function BecomeRetailer() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [geo, setGeo] = useState(null);            // {lat, lng}
  const [geoLoading, setGeoLoading] = useState(false);
  const [form, setForm] = useState({
    gst_number: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    ref_code: "",
  });
  const [refValidation, setRefValidation] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/retailer/status");
      setStatus(data);
      if (data.retailer_application) {
        const a = data.retailer_application;
        setForm({
          gst_number: a.gst_number || "",
          address: a.address || "",
          city: a.city || "",
          state: a.state || "",
          pincode: a.pincode || "",
        });
        if (a.latitude && a.longitude) setGeo({ lat: a.latitude, lng: a.longitude });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setGeo({ lat, lng });
        // Reverse geocode via Nominatim (OpenStreetMap) — free public endpoint
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
            { headers: { "Accept-Language": "en" } }
          );
          const j = await r.json();
          const a = j.address || {};
          setForm((f) => ({
            ...f,
            address: j.display_name || f.address,
            city: a.city || a.town || a.village || a.county || f.city,
            state: a.state || f.state,
            pincode: a.postcode || f.pincode,
          }));
          toast.success("Location captured · confirm or edit the address below");
        } catch {
          toast.info("Coordinates captured. Please fill the address manually.");
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        toast.error(err.message || "Could not get location");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!GST_PATTERN.test(form.gst_number)) {
      toast.error("GST number format is invalid (expected 15-char GSTIN)");
      return;
    }
    if (!geo) {
      toast.error("Please capture your location first");
      return;
    }
    if (!form.address || form.address.length < 5) {
      toast.error("Please confirm the address");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/retailer/apply", {
        gst_number: form.gst_number,
        latitude: geo.lat,
        longitude: geo.lng,
        address: form.address,
        city: form.city || null,
        state: form.state || null,
        pincode: form.pincode || null,
        ref_code: form.ref_code || null,
      });
      toast.success("Application submitted · admin will review shortly");
      await refreshUser();
      await loadStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const rs = status?.retailer_status || "not_applied";

  return (
    <DashboardLayout>
      <div className="animate-fade-up max-w-3xl">
        <span className="overline">Retailer onboarding</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
          Become a Retailer
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl">
          Retailer access unlocks BBPS bill payments, payouts, beneficiary management and
          the 3-level approval workflow. Submit a short application for admin approval.
        </p>

        {loading ? (
          <div className="mt-8 surface-card p-12 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rs === "verified" || user?.role === "partner" ? (
          <div data-testid="retailer-verified" className="mt-8 surface-card p-6 flex items-start gap-4 border-success/40">
            <CheckCircle2 className="h-6 w-6 text-success shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="font-semibold">You are a verified Retailer</div>
              <p className="text-sm text-muted-foreground mt-1">
                Head over to your dashboard to start processing payments.
              </p>
            </div>
          </div>
        ) : rs === "pending" ? (
          <div data-testid="retailer-pending" className="mt-8 surface-card p-6 flex items-start gap-4 border-primary/30">
            <Clock className="h-6 w-6 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="font-semibold">Application under review</div>
              <p className="text-sm text-muted-foreground mt-1">
                Admin typically decides within 24 hours. We'll notify you as soon as it's approved.
              </p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <KV k="GST" v={status?.retailer_application?.gst_number} />
                <KV k="City" v={status?.retailer_application?.city} />
                <KV k="Pincode" v={status?.retailer_application?.pincode} />
              </div>
            </div>
          </div>
        ) : (
          <>
            {rs === "rejected" && (
              <div data-testid="retailer-rejected" className="mt-8 surface-card p-5 border-destructive/40 flex items-start gap-3">
                <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="font-semibold text-sm">Previous application rejected</div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Reason: <em>{status?.retailer_rejection_reason || "Not specified"}</em>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">You can re-submit with corrections below.</p>
                </div>
              </div>
            )}

            <form onSubmit={submit} className="mt-8 surface-card p-6 space-y-6" data-testid="retailer-form">
              <div>
                <Label htmlFor="gst">GST Number (GSTIN)</Label>
                <Input
                  id="gst"
                  data-testid="gst-input"
                  value={form.gst_number}
                  onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })}
                  placeholder="27ABCDE1234F1Z5"
                  maxLength={15}
                  className="mt-1 font-mono tracking-wider"
                  required
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  15-character GSTIN as per the GST portal. Will be verified against India GST records.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Business location</Label>
                  {geo && (
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {geo.lat.toFixed(4)}, {geo.lng.toFixed(4)}
                    </Badge>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={captureLocation}
                  variant="outline"
                  disabled={geoLoading}
                  data-testid="capture-location"
                  className="mt-2"
                >
                  {geoLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <MapPin className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  )}
                  {geo ? "Refresh my location" : "Capture my current location"}
                </Button>
                <p className="text-[11px] text-muted-foreground mt-1">
                  We use your browser's geolocation + OpenStreetMap to pre-fill the address. Please confirm before submitting.
                </p>
              </div>

              <div>
                <Label htmlFor="addr">Address (confirm/edit)</Label>
                <textarea
                  id="addr"
                  data-testid="address-input"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={3}
                  className="mt-1 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm"
                  placeholder="Full business address"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" data-testid="city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input id="state" data-testid="state-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="pin">Pincode</Label>
                  <Input id="pin" data-testid="pincode-input" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} maxLength={6} className="mt-1 font-mono" />
                </div>
              </div>

              <div>
                <Label htmlFor="rcode">Referral / Promo code <span className="text-muted-foreground font-normal text-xs">(optional · sets your subscription tier)</span></Label>
                <Input
                  id="rcode"
                  data-testid="ref-code-input"
                  className="mt-1 font-mono tracking-wider uppercase"
                  value={form.ref_code}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "");
                    setForm({ ...form, ref_code: v });
                    setRefValidation(null);
                  }}
                  onBlur={async () => {
                    if (!form.ref_code) return setRefValidation(null);
                    try {
                      const { data: j } = await api.get(`/refcodes/${form.ref_code}/validate`);
                      setRefValidation({ valid: true, sub: j.subscription });
                    } catch { setRefValidation({ valid: false }); }
                  }}
                  placeholder="WELCOME"
                />
                {refValidation?.valid && (
                  <p className="text-xs text-success mt-1" data-testid="ref-valid">
                    ✓ Valid · activates {refValidation.sub.name} ({refValidation.sub.allowed_rails?.join(", ").toUpperCase()})
                  </p>
                )}
                {refValidation?.valid === false && form.ref_code && (
                  <p className="text-xs text-destructive mt-1" data-testid="ref-invalid">Invalid or fully-used code</p>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button type="submit" disabled={submitting || !geo} data-testid="submit-retailer">
                  {submitting ? "Submitting..." : "Submit for admin approval"}
                  <ShieldCheck className="h-4 w-4 ml-1" strokeWidth={1.5} />
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

const KV = ({ k, v }) => (
  <div className="border border-border rounded-md p-2">
    <div className="overline text-[9px]">{k}</div>
    <div className="font-mono text-xs truncate mt-0.5">{v || "—"}</div>
  </div>
);
